import { type ReactNode } from 'react'
import { cn } from '../../utils.js'

export interface BottomTabItem {
  /** Unique identifier */
  id: string
  /** Label text shown below icon */
  label: string
  /** Icon element */
  icon: ReactNode
  /** Whether this tab is active */
  active?: boolean
  /** Click handler */
  onClick?: () => void
}

export interface BottomTabBarProps {
  /** Tab items to render */
  items: BottomTabItem[]
  className?: string
}

/**
 * Mobile bottom navigation tab bar.
 *
 * Fixed to the bottom of the viewport on small screens. Each tab has an icon
 * and label with a minimum 44x44px touch target. Hidden on md+ (desktop uses
 * the sidebar instead).
 */
export function BottomTabBar({ items, className }: BottomTabBarProps) {
  return (
    <nav
      aria-label="Mobile navigation"
      className={cn(
        'fixed bottom-0 inset-x-0 z-[var(--z-sticky)]',
        'flex items-stretch',
        'bg-[var(--color-background)] border-t border-[var(--color-border)]',
        'safe-area-bottom',
        // Only visible on mobile (< md)
        'md:hidden',
        className,
      )}
    >
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={item.onClick}
          aria-current={item.active ? 'page' : undefined}
          aria-label={item.label}
          className={cn(
            // Touch target: minimum 44px tall, equal flex width
            'flex flex-1 flex-col items-center justify-center gap-0.5',
            'min-h-[44px] py-2 px-1',
            'text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-primary)]',
            item.active
              ? 'text-[var(--color-primary)]'
              : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]',
          )}
        >
          <span
            className={cn(
              'flex h-6 w-6 items-center justify-center transition-colors',
            )}
          >
            {item.icon}
          </span>
          <span className="truncate max-w-full leading-none">{item.label}</span>
        </button>
      ))}
    </nav>
  )
}
